<template>
  <!-- EcountEtl 실행 목록 (SPEC-ECOUNT-FE-POLISH-001 Phase B-1 — 딜러 패턴 답습) -->
  <div class="page-container-v2">
    <div class="page-header">
      <span class="page-title">{{ $t('ecountEtl_runs_title') }}</span>
      <div style="display: flex; align-items: center; gap: 8px; margin-left: auto;">
        <timezone-toggle :current-tz="currentTz" @toggle="toggleTz" />
        <refresh-toggle
          :enabled="autoRefreshEnabled"
          :last-fetch-at="lastFetchAt"
          :loading="loading"
          :seconds-until-next="secondsUntilNext"
          @toggle="onToggleAutoRefresh"
          @force-refresh="fetchData"
        />
        <a-button size="small" @click="showShortcutHelpRef = true">?</a-button>
      </div>
    </div>

    <!-- 필터 영역 -->
    <a-card style="margin-bottom: 16px;">
      <a-form layout="inline" class="dealer-filter-form" :model="filters" @finish="onSearch">
        <a-form-item :label="$t('ecountEtl_runs_filter_runId')">
          <a-input
            v-model:value="filters.runId"
            allow-clear
            style="min-width: 200px;"
            :placeholder="$t('ecountEtl_runs_filter_runId_placeholder')"
            @pressEnter="onSearch"
          />
        </a-form-item>
        <a-form-item :label="$t('ecountEtl_runs_filter_status')">
          <a-select
            v-model:value="filters.status"
            allow-clear
            style="min-width: 140px;"
            :placeholder="$t('ecountEtl_runs_filter_status')"
          >
            <a-select-option value="running">Running</a-select-option>
            <a-select-option value="success">Success</a-select-option>
            <a-select-option value="partial">Partial</a-select-option>
            <a-select-option value="failed">Failed</a-select-option>
            <a-select-option value="cancelled">Cancelled</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item :label="$t('ecountEtl_runs_filter_dateRange')">
          <a-range-picker v-model:value="filters.dateRange" style="width: 240px;" />
        </a-form-item>
        <a-form-item class="filter-actions">
          <a-space>
            <a-button type="primary" html-type="submit" :loading="loading">
              {{ $t('ecountEtl_common_search') }}
            </a-button>
            <a-button @click="onReset">{{ $t('ecountEtl_common_reset') }}</a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <!-- 오류 표시 -->
    <a-alert
      v-if="error"
      type="error"
      :message="error"
      style="margin-bottom: 16px;"
      show-icon
      closable
      @close="error = null"
    />

    <!-- 툴바: 총건수 (좌) + 정렬·페이지당 (우) -->
    <div class="toolbar-row">
      <span class="total-count">
        {{ $t('ecountEtl_common_total') }}: {{ formatNumber(pagination.total) }}
      </span>
      <div class="toolbar-controls">
        <a-select v-model:value="pageSize" size="small" style="width: 100px;" @change="onToolbarPageSizeChange">
          <a-select-option :value="10">10 / {{ $t('ecountEtl_common_page') }}</a-select-option>
          <a-select-option :value="20">20 / {{ $t('ecountEtl_common_page') }}</a-select-option>
          <a-select-option :value="50">50 / {{ $t('ecountEtl_common_page') }}</a-select-option>
          <a-select-option :value="100">100 / {{ $t('ecountEtl_common_page') }}</a-select-option>
        </a-select>
      </div>
    </div>

    <!-- 데스크탑: a-table (가로 스크롤) -->
    <a-table
      v-if="!isMobileView"
      :data-source="runs"
      :columns="columns"
      :loading="loading"
      :pagination="false"
      row-key="runId"
      size="small"
      :scroll="{ x: 1100 }"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'status'">
          <run-status-badge :value="record.status" />
        </template>
        <template v-else-if="column.key === 'startedAt'">
          {{ formatDateTime(record.startedAt) }}
        </template>
        <template v-else-if="column.key === 'finishedAt'">
          {{ formatDateTime(record.finishedAt) }}
        </template>
        <template v-else-if="['fetchedRows','inserted','updated','failed'].includes(column.key)">
          {{ formatNumber(record[column.dataIndex]) }}
        </template>
        <template v-else-if="column.key === 'durationMs'">
          {{ formatDuration(record.durationMs) }}
        </template>
        <template v-else-if="column.key === 'actions'">
          <a-button size="small" type="link" @click="goToDetail(record.runId)">
            {{ $t('ecountEtl_runs_detail') }}
          </a-button>
        </template>
      </template>
    </a-table>

    <!-- 모바일: 카드뷰 -->
    <div v-else class="mobile-card-list">
      <div v-if="runs.length === 0 && !loading" class="empty-state">
        <a-empty :description="$t('ecountEtl_common_empty')" />
      </div>
      <div v-for="record in runs" :key="record.runId" class="mobile-card">
        <div class="card-header">
          <run-status-badge :value="record.status" />
        </div>
        <div class="card-field">
          <span class="card-label">{{ $t('ecountEtl_runs_col_runId') }}</span>
          <span class="card-value mono">{{ truncate(record.runId, 36) }}</span>
        </div>
        <div class="card-field">
          <span class="card-label">{{ $t('ecountEtl_runs_col_startedAt') }}</span>
          <span class="card-value">{{ formatDateTime(record.startedAt) }}</span>
        </div>
        <div class="card-field">
          <span class="card-label">{{ $t('ecountEtl_runs_col_finishedAt') }}</span>
          <span class="card-value">{{ formatDateTime(record.finishedAt) }}</span>
        </div>
        <div class="card-field">
          <span class="card-label">{{ $t('ecountEtl_runs_col_durationMs') }}</span>
          <span class="card-value">{{ formatDuration(record.durationMs) }}</span>
        </div>
        <div class="card-metrics-grid">
          <div class="metric-item">
            <span class="metric-label">{{ $t('ecountEtl_runs_col_fetchedRows') }}</span>
            <span class="metric-value">{{ formatNumber(record.fetchedRows) }}</span>
          </div>
          <div class="metric-item">
            <span class="metric-label">{{ $t('ecountEtl_runs_col_inserted') }}</span>
            <span class="metric-value">{{ formatNumber(record.inserted) }}</span>
          </div>
          <div class="metric-item">
            <span class="metric-label">{{ $t('ecountEtl_runs_col_updated') }}</span>
            <span class="metric-value">{{ formatNumber(record.updated) }}</span>
          </div>
          <div class="metric-item">
            <span class="metric-label">{{ $t('ecountEtl_runs_col_failed') }}</span>
            <span class="metric-value" :class="{ 'metric-error': Number(record.failed) > 0 }">
              {{ formatNumber(record.failed) }}
            </span>
          </div>
        </div>
        <div class="card-actions">
          <a-button size="small" type="link" @click="goToDetail(record.runId)">
            {{ $t('ecountEtl_runs_detail') }}
          </a-button>
        </div>
      </div>
    </div>

    <!-- 페이지네이션 분리 (우측 정렬, show-total + size-changer) -->
    <div class="pagination-row">
      <a-pagination
        v-model:current="pagination.current"
        :total="pagination.total"
        :page-size="pageSize"
        :show-total="totalLabel"
        show-size-changer
        :page-size-options="['10', '20', '50', '100']"
        @change="onPageChange"
        @show-size-change="onPageSizeChange"
      />
    </div>

    <shortcut-help v-model:visible="showShortcutHelpRef" />
  </div>
</template>

<script>
// @MX:SPEC: SPEC-ECOUNT-FE-POLISH-001 Phase B-1 — 딜러 패턴 답습 + mixin

import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAutoRefresh } from './composables/useAutoRefresh.js';
import { useKstTime } from './composables/useKstTime.js';
import { useShortcuts } from './composables/useShortcuts.js';
import { getRuns, extractApiError, parsePaginatedResponse } from '../../apis/ecountEtl.js';
import RunStatusBadge from './_components/RunStatusBadge.vue';
import RefreshToggle from './_components/RefreshToggle.vue';
import TimezoneToggle from './_components/TimezoneToggle.vue';
import ShortcutHelp from './_components/ShortcutHelp.vue';
import { RouterEnum } from '../../enums/router.enums.js';
import dealerEcountListMixin from '@/mixins/dealerEcountListMixin';
import { QuestionCircleOutlined } from '@ant-design/icons-vue';

export default {
  name: 'EcountEtlRunsList',
  components: { RunStatusBadge, RefreshToggle, TimezoneToggle, ShortcutHelp, QuestionCircleOutlined },
  mixins: [dealerEcountListMixin],

  setup() {
    const { currentTz, toggleTz } = useKstTime();
    const {
      enabled: autoRefreshEnabled,
      toggle: toggleAutoRefresh,
      lastFetchAt,
      secondsUntilNext,
    } = useAutoRefresh({ intervalMs: 15000, storageKey: 'ecountEtl.runsAutoRefresh' });

    const router = useRouter();
    const showShortcutHelpRef = ref(false);

    useShortcuts({
      '?': () => { showShortcutHelpRef.value = true; },
      'g+d': () => router.push({ name: RouterEnum.EcountEtl.Dashboard.name }),
      'g+r': () => router.push({ name: RouterEnum.EcountEtl.Runs.name }),
      'g+x': () => router.push({ name: RouterEnum.EcountEtl.DeadLetters.name }),
      'g+p': () => router.push({ name: RouterEnum.EcountEtl.Products.name }),
      'g+t': () => router.push({ name: RouterEnum.EcountEtl.Transactions.name }),
    });

    return { currentTz, toggleTz, autoRefreshEnabled, toggleAutoRefresh, lastFetchAt, secondsUntilNext, showShortcutHelpRef };
  },

  data() {
    return {
      loading: false,
      error: null,
      runs: [],
      pageSize: 20,
      pagination: { current: 1, total: 0 },
      filters: {
        runId: '',
        status: undefined,
        dateRange: null,
      },
    };
  },

  computed: {
    columns() {
      // 날짜 컬럼 헤더 + TZ 안내 ? 툴팁
      const dateColTitle = (labelKey) => () => (
        <span style="display: inline-flex; align-items: center; gap: 4px;">
          {this.$t(labelKey)}
          <a-tooltip title={this.$t('dealerGsp_tz_tooltip', { tz: this.timezoneInfo.label })}>
            <QuestionCircleOutlined style="color: rgba(0, 0, 0, 0.45); cursor: help" />
          </a-tooltip>
        </span>
      );
      return [
        { key: 'runId', dataIndex: 'runId', title: this.$t('ecountEtl_runs_col_runId'), width: 250, ellipsis: true },
        { key: 'status', dataIndex: 'status', title: this.$t('ecountEtl_runs_col_status'), width: 110 },
        { key: 'startedAt', dataIndex: 'startedAt', title: dateColTitle('ecountEtl_runs_col_startedAt'), width: 170 },
        { key: 'finishedAt', dataIndex: 'finishedAt', title: dateColTitle('ecountEtl_runs_col_finishedAt'), width: 170 },
        { key: 'durationMs', dataIndex: 'durationMs', title: this.$t('ecountEtl_runs_col_durationMs'), width: 100, align: 'right' },
        { key: 'fetchedRows', dataIndex: 'fetchedRows', title: this.$t('ecountEtl_runs_col_fetchedRows'), width: 100, align: 'right' },
        { key: 'inserted', dataIndex: 'inserted', title: this.$t('ecountEtl_runs_col_inserted'), width: 100, align: 'right' },
        { key: 'updated', dataIndex: 'updated', title: this.$t('ecountEtl_runs_col_updated'), width: 100, align: 'right' },
        { key: 'failed', dataIndex: 'failed', title: this.$t('ecountEtl_runs_col_failed'), width: 90, align: 'right' },
        { key: 'actions', title: this.$t('ecountEtl_runs_col_actions'), width: 80, fixed: 'right' },
      ];
    },
  },

  mounted() {
    this.applyQueryToFilters();
    this.fetchData();
  },

  methods: {
    onToggleAutoRefresh(val) { this.toggleAutoRefresh(val); },
    onSearch() { this.pagination.current = 1; this.fetchData(); },
    onReset() {
      this.filters = { runId: '', status: undefined, dateRange: null };
      this.pagination.current = 1;
      this.fetchData();
    },
    async fetchData() {
      this.loading = true;
      this.error = null;
      try {
        const params = {
          page: this.pagination.current,
          limit: this.pageSize,
          status: this.filters.status,
        };
        if (this.filters.runId && this.filters.runId.trim()) {
          params.runId = this.filters.runId.trim();
        }
        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          params.startDate = this.filters.dateRange[0].toISOString();
          params.endDate = this.filters.dateRange[1].toISOString();
        }
        const res = await getRuns(params);
        const parsed = parsePaginatedResponse(res);
        this.runs = parsed.items || parsed.data || [];
        this.pagination.total = parsed.total ?? 0;
      } catch (e) {
        const err = extractApiError(e);
        const localized = this.$t(err.messageKey, err.messageParams);
        this.error = localized.startsWith('ecountEtl_') ? err.message : localized;
        this.runs = [];
        this.pagination.total = 0;
      } finally {
        this.loading = false;
      }
    },
    goToDetail(runId) {
      this.$router.push({ name: RouterEnum.EcountEtl.RunDetail.name, params: { runId } });
    },
  },
};
</script>

<style scoped>
.page-header { display: flex; align-items: center; margin-bottom: 16px; }
.page-title { font-size: 20px; font-weight: 600; }
.filter-actions { margin-bottom: 0; margin-left: auto; margin-right: 0; }

.dealer-filter-form :deep(.ant-form-item) { margin-bottom: 12px; }
.dealer-filter-form :deep(.ant-form-item:last-child) { margin-bottom: 0; }
.filter-actions :deep(.ant-space) { flex-wrap: nowrap; }

.toolbar-row {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 12px; padding: 0 4px;
}
.total-count { font-size: 13px; color: #666; }
.toolbar-controls { display: flex; gap: 8px; }

.pagination-row { display: flex; justify-content: flex-end; margin-top: 16px; }

/* 모바일 카드뷰 */
.mobile-card-list { display: flex; flex-direction: column; gap: 12px; }
.empty-state { padding: 32px 0; text-align: center; }
.mobile-card {
  background: #fff; border: 1px solid #f0f0f0; border-radius: 8px;
  padding: 12px; box-shadow: 0 1px 2px rgba(0,0,0,0.04);
}
.card-header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid #f5f5f5;
}
.card-job-type { font-weight: 600; color: #333; font-size: 13px; }
.card-field {
  display: flex; justify-content: space-between; align-items: baseline;
  font-size: 12px; padding: 4px 0;
}
.card-label { color: #888; flex-shrink: 0; margin-right: 8px; }
.card-value { color: #333; text-align: right; word-break: break-all; }
.card-value.mono { font-family: monospace; font-size: 11px; }
.card-metrics-grid {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px;
  margin: 8px 0; padding: 8px; background: #fafafa; border-radius: 4px;
}
.metric-item { display: flex; flex-direction: column; align-items: center; gap: 2px; }
.metric-label { font-size: 10px; color: #999; }
.metric-value { font-size: 13px; font-weight: 600; color: #333; font-family: monospace; }
.metric-value.metric-error { color: #c62828; }
.card-actions {
  display: grid; grid-template-columns: 1fr; gap: 4px;
  margin-top: 8px; padding-top: 8px; border-top: 1px solid #f5f5f5;
}
.card-actions :deep(.ant-btn) { width: 100%; }

@media (max-width: 767px) {
  .page-header { flex-wrap: wrap; gap: 8px; row-gap: 8px; }
  .toolbar-row { flex-direction: column; align-items: stretch; gap: 8px; }
  .toolbar-controls { justify-content: flex-end; }
  .pagination-row { justify-content: center; }
}
</style>
